import * as React from 'react'
import { CloseReason, OpenVia } from '../searchSession'

export const OPEN_GLOBAL_SEARCH_EVENT = 'globalSearch:open'
// Broadcast whenever the overlay opens or closes, so the sidebar trigger can
// mirror the state (pressed while open). detail: { open: boolean }
export const GLOBAL_SEARCH_STATE_EVENT = 'globalSearch:state'

// Open/close state for the overlay: Cmd/Ctrl+K toggle, Escape close and the
// sidebar trigger's custom event. Focus returns to the element that had it
// before opening. `onEscape` may consume an Escape press (returns true) for
// graduated behavior — first press clears the query, second closes.
// `onOpen`/`onClose` receive how the overlay was opened/closed; a close without
// a reason means the caller already handled the outcome (a result was opened).
export const useGlobalSearchOverlay = (
    onOpen?: (via: OpenVia) => void,
    onEscape?: () => boolean,
    onClose?: (reason?: CloseReason) => void,
) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const onOpenRef = React.useRef(onOpen)
    onOpenRef.current = onOpen
    const onEscapeRef = React.useRef(onEscape)
    onEscapeRef.current = onEscape
    const onCloseRef = React.useRef(onClose)
    onCloseRef.current = onClose
    const previousFocusRef = React.useRef<HTMLElement | null>(null)

    React.useEffect(() => {
        window.dispatchEvent(new CustomEvent(GLOBAL_SEARCH_STATE_EVENT, { detail: { open: isOpen } }))
        if (!isOpen && previousFocusRef.current) {
            previousFocusRef.current.focus()
            previousFocusRef.current = null
        }
    }, [isOpen])

    const open = React.useCallback((via: OpenVia = 'trigger') => {
        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        onOpenRef.current?.(via)
        setIsOpen(true)
    }, [])

    const close = React.useCallback((reason?: CloseReason) => {
        onCloseRef.current?.(reason)
        setIsOpen(false)
    }, [])

    React.useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                isOpen ? close('shortcut') : open('shortcut')
            } else if (event.key === 'Escape' && isOpen) {
                if (!onEscapeRef.current?.()) {
                    close('escape')
                }
            }
        }
        const onOpenEvent = () => open('trigger')
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener(OPEN_GLOBAL_SEARCH_EVENT, onOpenEvent)
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener(OPEN_GLOBAL_SEARCH_EVENT, onOpenEvent)
        }
    }, [isOpen, open, close])

    return { isOpen, open, close }
}
